import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Brain, Activity, PhoneOff, Radar, FileCheck2, ArrowUpRight, CheckCircle,
  LayoutGrid, BarChart3, Users, Trophy, PhoneCall, ShieldOff, Phone, Target, Coins, ChevronLeft,
  XCircle, Globe, Ban, Bot, ShoppingCart, ShieldAlert, Clock,
} from "lucide-react";

const STATS = [
  { value: "10M+", label: "Calls Analyzed" },
  { value: "100%", label: "Calls Verified" },
  { value: "97%+", label: "Valid Call Rate" },
  { value: "99%", label: "Detection Accuracy" },
];

const FEATURES = [
  { icon: Brain, title: "AI Fraud Detection", desc: "Every call is scored by our AI for bots, repeat callers, and spoofed numbers." },
  { icon: Radar, title: "Real-Time Monitoring", desc: "Live publisher and campaign monitoring 24/7 — issues flagged in seconds." },
  { icon: PhoneOff, title: "Spam & Robocall Filtering", desc: "Spam, junk, and robocalls are stopped before they ever reach a buyer." },
  { icon: FileCheck2, title: "Call Quality Verification", desc: "Duration, intent, and post-IVR buffer checked on every single call." },
];

const NAV = [
  ["Core", [[LayoutGrid, "Dashboard", true], [BarChart3, "Analytics"]]],
  ["Publisher Control", [[Users, "Publisher Report"], [Activity, "Publisher Activity"], [Trophy, "Publisher Ranking"], [PhoneCall, "DID Status"], [ShieldOff, "Publisher Exclusions"]]],
  ["Call Intelligence", [[Phone, "Call Analysis"], [Target, "Action Center"]]],
  ["Revenue Intelligence", [[Coins, "Buyer Optimizer"]]],
];

const PROBLEMS = [
  { icon: XCircle, name: "Fake Calls", sub: "Spam, scam, irrelevant", n: "9,412", c: "text-red-400" },
  { icon: Globe, name: "Non-US Accent", sub: "Foreign callers", n: "8,207", c: "text-orange-400" },
  { icon: Ban, name: "Adult Calls", sub: "Inappropriate content", n: "318", c: "text-purple-400" },
  { icon: Bot, name: "IVR/Robot", sub: "Automated callers", n: "6,946", c: "text-blue-400" },
  { icon: ShoppingCart, name: "Buy/Sell Intent", sub: "Trying to sell products", n: "1,105", c: "text-cyan-400" },
  { icon: Ban, name: "Blacklisted Products", sub: "Restricted product mention", n: "372", c: "text-rose-400" },
  { icon: ShieldAlert, name: "Behavioral Spam", sub: "2+ offers OR 10+ calls in 5min — grouped by caller", n: "5,634", c: "text-rose-400" },
  { icon: Clock, name: "Pending Analysis", sub: "Awaiting AI review", n: "1,204", c: "text-yellow-400" },
];

const KPIS = [
  ["Total Calls", "1,284,560", "text-white"],
  ["Valid Calls", "1,251,362", "text-emerald-400"],
  ["Flagged & Blocked", null, "text-emerald-400"],
  ["Active Publishers", "1,126", "text-blue-400"],
];

const SEGMENTS = [
  { pct: 97.4, color: "#4ade80" },
  { pct: 0.5, color: "#3b82f6" },
  { pct: 0.6, color: "#f59e0b" },
  { pct: 1.5, color: "#ef4444" },
];

const Sidebar = () => (
  <aside className="hidden md:flex w-52 flex-shrink-0 flex-col border-r border-slate-800 bg-[#0d1117]">
    <div className="flex items-center gap-2.5 px-4 py-4 border-b border-slate-800">
      <img src="/logo192.png" onError={(e) => { e.currentTarget.style.display = "none"; }} alt="" className="w-8 h-8 rounded-full bg-white object-contain" />
      <div className="leading-tight">
        <p className="text-white text-sm font-semibold">Callintell™</p>
        <p className="text-[9px] tracking-[0.15em] text-slate-500">DIGITAL MASTER MEDIA</p>
      </div>
      <ChevronLeft className="w-4 h-4 text-slate-500 ml-auto" />
    </div>
    <nav className="flex-1 px-3 py-3 space-y-4">
      {NAV.map(([group, items]) => (
        <div key={group}>
          <p className="px-2 text-[9px] tracking-[0.18em] uppercase text-slate-500 mb-1.5">{group}</p>
          {items.map(([Icon, label, active]) => (
            <div key={label} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[12px] ${active ? "bg-blue-500/15 text-blue-300 border-l-2 border-blue-500" : "text-slate-300"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </div>
          ))}
        </div>
      ))}
    </nav>
    <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-2 text-[11px] text-slate-400">
      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> System Online
    </div>
  </aside>
);

const ProblemBreakdown = () => (
  <div className="rounded-xl border border-slate-800 bg-[#11151c]" data-testid="callintell-problem-breakdown">
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-800">
      <ShieldCheck className="w-4 h-4 text-emerald-400" />
      <p className="text-white text-sm font-semibold">AI Quality Checks</p>
    </div>
    <div className="px-5 py-2">
      {PROBLEMS.map((p) => (
        <div key={p.name} className="flex items-center gap-3 py-2.5">
          <p.icon className={`w-4 h-4 ${p.c} flex-shrink-0`} />
          <div className="min-w-0 flex-1">
            <p className="text-slate-100 text-[13px]">{p.name}</p>
            <p className="text-slate-500 text-[10.5px] truncate">{p.sub}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-semibold" data-testid={`callintell-check-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <CheckCircle className="w-4 h-4" strokeWidth={2.5} /> Checked
          </span>
        </div>
      ))}
    </div>
  </div>
);

const Donut = () => {
  const r = 60;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 160 160" className="w-44 h-44 -rotate-90">
      {SEGMENTS.map((s) => {
        const len = (s.pct / 100) * circ;
        const el = <circle key={s.color} cx="80" cy="80" r={r} fill="none" stroke={s.color} strokeWidth="18" strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset} />;
        offset += len;
        return el;
      })}
    </svg>
  );
};

const CallDistribution = () => (
  <div className="rounded-xl border border-slate-800 bg-[#11151c] flex flex-col" data-testid="callintell-call-distribution">
    <div className="px-5 py-3.5 border-b border-slate-800">
      <p className="text-white text-sm font-semibold">Call Distribution</p>
    </div>
    <div className="flex-1 flex flex-col items-center justify-center py-8 px-4">
      <div className="relative flex items-center">
        <span className="hidden sm:block absolute -left-24 top-4 text-emerald-400 text-sm">Valid 97.4%</span>
        <Donut />
        <div className="hidden sm:block absolute -right-28 top-14 space-y-2 text-sm">
          <p className="text-blue-400">IVR/Robot 0.5%</p>
          <p className="text-amber-400">Non-US 0.6%</p>
          <p className="text-red-400">Other 1.5%</p>
        </div>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-4 text-[11px]">
        {[["Other", "bg-red-500", "text-red-400"], ["IVR/Robot", "bg-blue-500", "text-blue-400"], ["Non-US", "bg-amber-500", "text-amber-400"], ["Valid", "bg-emerald-400", "text-emerald-400"]].map(([l, d, t]) => (
          <span key={l} className={`inline-flex items-center gap-1.5 ${t}`}><span className={`w-2 h-2 rounded-full ${d}`} />{l}</span>
        ))}
      </div>
    </div>
  </div>
);

export const DashboardReplica = () => (
  <div className="rounded-2xl border border-slate-700 bg-[#0a0d12] overflow-hidden shadow-[0_30px_120px_-30px_rgba(59,130,246,0.55)]" data-testid="callintell-dashboard">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-[#161b22]">
      <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
      <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
      <span className="w-3 h-3 rounded-full bg-[#28c840]" />
      <div className="ml-4 flex-1 max-w-md mx-auto rounded-md bg-[#0d1117] border border-slate-800 px-3 py-1 text-[11px] text-slate-400 font-mono text-center">callintell.com/dashboard</div>
    </div>
    <div className="flex">
      <Sidebar />
      <div className="flex-1 min-w-0 p-4 sm:p-5 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {KPIS.map(([l, v, c]) => (
            <div key={l} className="rounded-xl border border-slate-800 bg-[#11151c] px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
              {v ? (
                <p className={`mt-1 text-lg sm:text-xl font-bold tabular-nums ${c}`}>{v}</p>
              ) : (
                <p className="mt-1 inline-flex items-center gap-1.5 text-lg sm:text-xl font-bold text-emerald-400" data-testid="callintell-flagged-check">
                  <CheckCircle className="w-5 h-5" strokeWidth={2.5} /> Checked
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ProblemBreakdown />
          <CallDistribution />
        </div>
      </div>
    </div>
  </div>
);

const Intro = () => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="max-w-3xl">
    <span className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 text-blue-300 text-xs font-semibold uppercase tracking-wider">
      <ShieldCheck className="w-4 h-4" /> Powered by CallIntell™ AI
    </span>
    <h2 className="mt-5 text-3xl sm:text-4xl font-bold text-white leading-tight">
      Every Call Checked & Verified by Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI Call Intelligence</span>
    </h2>
    <p className="mt-5 text-slate-400 leading-relaxed">
      CallIntell™ is our in-house AI call intelligence platform, built by Digital Master Media. Every call that moves through our network is analyzed in real time — screened for fraud, spam, robots, and quality — so buyers only pay for genuine customers and publishers are paid for every legitimate call.
    </p>
  </motion.div>
);

export const CallIntellShowcase = () => (
  <section className="py-20 sm:py-28 bg-slate-900 border-t border-slate-800 overflow-hidden" data-testid="callintell-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <Intro />
        <Link to="/callintell" data-testid="callintell-visit-link" className="self-start lg:self-auto inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold px-7 py-3 rounded-xl shadow-lg shadow-blue-600/25 hover:-translate-y-0.5 transition-transform text-sm">
          Explore CallIntell™ <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="mt-12 relative">
        <div className="absolute inset-x-10 -inset-y-6 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-3xl blur-3xl" />
        <div className="relative"><DashboardReplica /></div>
      </motion.div>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {FEATURES.map((f) => (
          <div key={f.title} className="flex gap-3" data-testid={`callintell-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
              <f.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">{f.title}</h3>
              <p className="mt-1 text-xs text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="callintell-stats">
        {STATS.map((s) => (
          <div key={s.label} className="rounded-2xl border border-slate-700/70 bg-slate-800/40 backdrop-blur-xl p-6 text-center">
            <p className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{s.value}</p>
            <p className="mt-2 text-xs uppercase tracking-wider text-slate-400 font-semibold">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
        {["TCPA-Compliant Calls", "24/7 Real-Time Monitoring", "Verified Before Payout", "Live AI Scoring"].map((t) => (
          <span key={t} className="inline-flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" /> {t}
          </span>
        ))}
      </div>
    </div>
  </section>
);
